import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './Founder.css';

const Founder = () => {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in-view'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.fd-reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="fd-root">
      <Helmet>
        <title>A note from the founder · dmyor</title>
        <link rel="canonical" href="https://www.dmyor.com/founder" />
        <meta name="description" content="Why I built dmyor: a note from the founder on design my own reality, and turning intentions into something that lasts." />
        <meta property="og:title" content="A note from the founder · dmyor" />
        <meta property="og:description" content="Why I built dmyor: a personal note on turning intentions into reality, and preserving the journey." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dmyor.com/founder" />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="fd-page">

        <span className="fd-eyebrow fd-reveal">A note from the founder</span>

        <h1 className="fd-hero fd-reveal">
          Why I built <span className="fd-clay">dmyor.</span>
        </h1>

        <div className="fd-letter">
          <p className="fd-p fd-reveal">
            Hi. If you're reading this, you probably landed on dmyor somehow. I wanted a
            page to explain what dmyor means to me, and why I built it.
          </p>

          <p className="fd-p fd-reveal">
            I want to try a lot of things. Start businesses, learn constantly, play, travel
            the world, and so much else I can't even remember right now. All of it is part of
            me, part of where I want to put my effort. I don't know if every one of them will
            work out, but this is how I want my life to look. And I want what's in my head to
            stay somewhere, forever, even when I'm gone. So I built dmyor.
          </p>

          <p className="fd-p fd-reveal">
            dmyor stands for <span className="fd-em">design my own reality</span>. To me it
            means this: what I write on dmyor is meant to happen. I'm going to put in the work
            to turn these words into something real. I want my life to be directional. I have
            goals, I want to know how to reach them, and I want to move that way on purpose,
            without wavering. It would be pretty amazing if, 20 years from now, everything I
            wrote here had come true. I believe it can, if I make the right decisions and
            don't stop.
          </p>

          <p className="fd-p fd-reveal">
            I also wanted to track my progress, document the journey, and make the work itself
            easier and faster. So I built a planning system that breaks a big vision down into
            small, actionable steps I can start on today. One space for planning, one for
            executing, and one for building the habits that quietly shape the vision over time.
          </p>

          <p className="fd-p fd-reveal">
            I call it an intent archive because everything on dmyor starts as just a thought in
            someone's mind. Your actions and decisions are what carry it into the real world.
            Until then, it's all intention. That journey, from intention to existence, is what
            I want dmyor to hold. I kind of like that idea, if I say so myself. :)
          </p>

          <p className="fd-p fd-reveal">
            If you read this far, you now know what dmyor is meant to be. Hopefully you'll write
            your own visions here too, and plan them into reality. Maybe a few decades from now,
            we get to see whose dreams actually came true.
          </p>

          <p className="fd-p fd-thanks fd-reveal">Thanks.</p>
        </div>

        <div className="fd-end fd-reveal">
          <Link to="/dashboard/planning/new" className="fd-btn">Start your first vision</Link>
          <p className="fd-sig">dmyor. Make it real.</p>
        </div>

      </div>
    </div>
  );
};

export default Founder;
